/**
 * Embeddable Widget Component
 *
 * A mobile-like React app for embedding Bolder features in external websites.
 * Features:
 * - Full mobile app experience with bottom navigation
 * - Theme support (presets and custom themes)
 * - Home page with profile, portals, quick actions
 * - Authentication (magic link + OTP)
 * - Configurable via props
 * - Multi-agent picker when multiple brains configured
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { EmbedApp } from './app/EmbedApp';
import { AgentOption } from './components/AgentSelectorDropdown';
import { debugLog } from './debug';
import { EmbedThemeConfig, resolveTheme, ThemePresetName } from './theme';
import type { AdvancedWidgetConfig } from './theme/types';

// Types for brain selection
export interface SelectedBrain {
  id: string;
  name: string;
  displayImage?: string;
  description?: string;
}

// ============================================================================
// Types
// ============================================================================

/**
 * Theme can be specified as:
 * - A preset name: 'light', 'dark', 'modern', 'minimal', 'bold', 'ocean', 'forest', 'rose', 'midnight', 'ember', 'rounded-sm', 'sharp'
 * - A custom theme config object with mode, primaryColor, fontFamily, borderRadius, colors
 */
export type WidgetTheme = ThemePresetName | EmbedThemeConfig;

export interface WidgetProps {
  /**
   * Theme of the widget
   *
   * Can be:
   * - A preset name: 'light', 'dark', 'modern', 'minimal', 'bold', 'ocean', 'forest', 'rose', 'midnight', 'ember', 'rounded-sm', 'sharp'
   * - A custom theme config object:
   *   {
   *     mode: 'light' | 'dark',
   *     primaryColor: '#hexcolor',
   *     fontFamily: 'font-name',
   *     borderRadius: 'none' | 'sm' | 'md' | 'lg' | 'full',
   *     colors: { background: {...}, text: {...}, border: {...} }
   *   }
   */
  theme?: WidgetTheme;
  /** Widget title */
  title?: string;
  /** Primary color (hex or CSS color) - overrides theme's primaryColor */
  primaryColor?: string;
  /** Callback when widget is ready */
  onReady?: () => void;
  /** Callback for widget events */
  onEvent?: (eventName: string, data: unknown) => void;
  /** Custom class name */
  className?: string;
  /** API endpoint base URL (e.g., https://api.qa.bldr.it) */
  apiBaseUrl?: string;
  /** GraphQL endpoint URL (e.g., https://api.qa.bldr.it/graphql) */
  graphqlUrl?: string;
  /** Asset Manager API URL for WebSocket and direct API calls (e.g., https://asset-api.qa.bldr.it) */
  assetApiUrl?: string;
  /** Media/Images base URL (e.g., https://images.qa.bldr.it/) */
  mediaBaseUrl?: string;
  /** App base URL for proxy requests (e.g., https://spaces.qa.bldr.it) */
  appBaseUrl?: string;
  /** Custom container style */
  style?: React.CSSProperties;
  /** Path to render specific UI (e.g., /brain/123) */
  path?: string;
  /** Owner user ID - used to show owner profile instead of default branding */
  id?: string;
  /** Owner handle - used to fetch owner profile for branding */
  ownerHandle?: string;
  /** Callback when editor config changes (for WordPress integration) */
  onConfigChange?: (config: any) => void;
  /** Skip authentication prompts - use guest mode for public content */
  guestMode?: boolean;
  /** Initial message to send automatically when the widget loads */
  initialMessage?: string;
  /** Show configure button that opens /distribute in a new tab (for external embeds) */
  showConfigureButton?: boolean;
  /** Agent icon style for AI messages: 'robot' | 'sparkle' | 'brain' | 'chat' | 'message' */
  agentIcon?: 'robot' | 'sparkle' | 'brain' | 'chat' | 'message';
  /** Force showing the login page regardless of authentication state */
  forceLogin?: boolean;
  /** Advanced widget configuration for fine-grained visual and behavioral customization */
  advancedConfig?: AdvancedWidgetConfig;
  /** Callback when authentication state changes */
  onAuthChange?: (isAuthenticated: boolean) => void;
  /** Selected brains for multi-agent picker */
  selectedBrains?: SelectedBrain[];
  /** Custom conversation starters (pills) — overrides the brain's launch prompts when set */
  conversationStarters?: string[];
  /** Chat bubble style: bubbles (default), minimal, or cards */
  chatStyle?: 'bubbles' | 'minimal' | 'cards';
  /** Header: override the brain's display name */
  displayNameOverride?: string;
  /** Header: subtitle line shown under the name */
  headerSubtitle?: string;
  /** Header: override the brain's display image (URL) */
  displayImageOverride?: string;
  /** Callback when collapse button is clicked (shows collapse button in header when provided) */
  onCollapse?: () => void;
  /** Headless mode - hide header/agent selector for embedding within another UI that provides its own */
  headless?: boolean;
}

// ============================================================================
// Main Widget Component
// ============================================================================

export const Widget: React.FC<WidgetProps> = ({
  theme,
  title = 'Bolder Space',
  primaryColor,
  onReady,
  onEvent,
  className = '',
  apiBaseUrl,
  graphqlUrl,
  assetApiUrl,
  mediaBaseUrl,
  appBaseUrl,
  style,
  path,
  id,
  ownerHandle,
  onConfigChange,
  guestMode = false,
  initialMessage,
  showConfigureButton = false,
  agentIcon = 'message',
  forceLogin = false,
  onAuthChange,
  selectedBrains = [],
  conversationStarters,
  chatStyle,
  displayNameOverride,
  headerSubtitle,
  displayImageOverride,
  onCollapse,
  advancedConfig,
  headless = false,
}) => {
  // Resolve theme - can be preset name string or config object
  const resolvedThemeConfig = useMemo(() => {
    const config = resolveTheme(theme);

    // If primaryColor prop is provided, it overrides the theme's primaryColor
    if (primaryColor) {
      return { ...config, primaryColor };
    }

    return config;
  }, [theme, primaryColor]);

  // Build agents array from selectedBrains
  const agents = useMemo<AgentOption[]>(() => {
    return selectedBrains.map((brain) => ({
      type: 'brain' as const,
      id: brain.id,
      name: brain.name,
      displayImage: brain.displayImage,
      description: brain.description,
    }));
  }, [selectedBrains]);

  // State for selected agent - initialize to first agent if available
  const [selectedAgent, setSelectedAgent] = useState<AgentOption | null>(() => {
    if (agents.length > 0) return agents[0];
    return null;
  });

  // Update selectedAgent when agents array changes
  useEffect(() => {
    if (agents.length === 0) {
      // No agents available, clear selection
      setSelectedAgent(null);
    } else if (!selectedAgent) {
      // No agent selected, select the first one
      setSelectedAgent(agents[0]);
    } else {
      // Check if current selectedAgent still exists in the new agents array
      const stillExists = agents.some((a) => a.type === selectedAgent.type && a.id === selectedAgent.id);
      if (!stillExists) {
        // Current selection no longer valid, select the first agent
        setSelectedAgent(agents[0]);
      }
    }
  }, [agents]);

  // Debug logging
  debugLog('[Widget] ========== WIDGET RENDER ==========');
  debugLog('[Widget] Props received:');
  debugLog('[Widget]   appBaseUrl:', appBaseUrl);
  debugLog('[Widget]   apiBaseUrl:', apiBaseUrl);
  debugLog('[Widget]   graphqlUrl:', graphqlUrl);
  debugLog('[Widget]   mediaBaseUrl:', mediaBaseUrl);
  debugLog('[Widget]   theme:', theme);
  debugLog('[Widget]   resolvedThemeConfig:', resolvedThemeConfig);
  debugLog('[Widget]   showConfigureButton:', showConfigureButton);
  debugLog('[Widget]   forceLogin:', forceLogin);
  debugLog('[Widget]   guestMode:', guestMode);
  debugLog('[Widget]   agents:', agents);
  debugLog('[Widget]   selectedAgent:', selectedAgent);

  // Determine GraphQL URL - graphqlUrl takes priority, then apiBaseUrl + /graphql
  const gqlUrl = graphqlUrl || (apiBaseUrl ? `${apiBaseUrl}/graphql` : '');
  debugLog('[Widget] Computed gqlUrl:', gqlUrl);

  // Extract mode and primaryColor from resolved config for EmbedApp
  const themeMode = resolvedThemeConfig.mode || 'light';
  const themePrimaryColor = resolvedThemeConfig.primaryColor || '#3b82f6';

  // Derive assetApiUrl if not provided
  const derivedAssetApiUrl =
    assetApiUrl || (apiBaseUrl ? apiBaseUrl.replace('://api.', '://asset-api.') : '');
  debugLog('[Widget] assetApiUrl:', assetApiUrl, 'derived:', derivedAssetApiUrl);

  // Compute effective path based on selected agent
  const effectivePath = useMemo(() => {
    // If explicit path provided, use it
    if (path) return path;

    // If agent selected, compute path
    if (selectedAgent) {
      return `/brain/${selectedAgent.id}`;
    }

    return undefined;
  }, [path, selectedAgent]);

  // Handle agent change from dropdown
  const handleAgentChange = useCallback((agent: AgentOption) => {
    debugLog('[Widget] Agent changed:', agent);
    setSelectedAgent(agent);
  }, []);

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        ...style,
      }}
    >
      <EmbedApp
        graphqlUrl={gqlUrl}
        apiBaseUrl={apiBaseUrl}
        appBaseUrl={appBaseUrl}
        mediaBaseUrl={mediaBaseUrl}
        assetApiUrl={derivedAssetApiUrl}
        theme={themeMode}
        primaryColor={themePrimaryColor}
        themeConfig={resolvedThemeConfig}
        title={title}
        path={effectivePath}
        id={id}
        ownerHandle={ownerHandle}
        onReady={onReady}
        onEvent={onEvent}
        onConfigChange={onConfigChange}
        className={className}
        guestMode={guestMode}
        initialMessage={initialMessage}
        agentIcon={agentIcon}
        forceLogin={forceLogin}
        onAuthChange={onAuthChange}
        agents={agents}
        selectedAgent={selectedAgent}
        onAgentChange={handleAgentChange}
        showConfigureButton={showConfigureButton}
        onCollapse={onCollapse}
        advancedConfig={advancedConfig}
        conversationStarters={conversationStarters}
        chatStyle={chatStyle}
        displayNameOverride={displayNameOverride}
        headerSubtitle={headerSubtitle}
        displayImageOverride={displayImageOverride}
        headless={headless}
      />
    </div>
  );
};

export default Widget;
